import React, { useEffect, useRef, useState } from "react";

import {
  TORCH_MODES,
  getTorchState,
  setTorchMode,
  refreshTorchSupport,
} from "../utils_new/torchController";

/**
 * Flash button for the camera view, modelled on a phone camera's flash control:
 * one small target that cycles, rather than a row of labelled options competing
 * with the viewfinder.
 *
 *   AUTO  bolt + "A", orange   torchController's brightness window drives the lamp
 *   ON    bolt + "M", orange   manual, lamp held on
 *   OFF   outlined bolt, white, crossed out
 *
 * Tapping moves to the next state and wraps, and names the new state in a badge
 * for a couple of seconds so the user is never left decoding the glyph to find
 * out what their tap did.
 *
 * The controller owns the truth: the handler re-renders from what its setter
 * returns, and a slow poll catches changes nobody tapped -- in AUTO the lamp
 * moves on its own, and resetTorch puts the mode back on every camera start.
 */

const POLL_MS = 500;
const BADGE_MS = 2000;

const ACTIVE = "#FF9F0A";
const IDLE = "#FFFFFF";
// Also the button ground, so a slash knocked out against it lines up exactly.
const DARK = "#101012";

const NEXT = {
  [TORCH_MODES.AUTO]: TORCH_MODES.ON,
  [TORCH_MODES.ON]: TORCH_MODES.OFF,
  [TORCH_MODES.OFF]: TORCH_MODES.AUTO,
};

const BADGE = {
  [TORCH_MODES.AUTO]: "Auto",
  [TORCH_MODES.ON]: "Manual",
  [TORCH_MODES.OFF]: "Turn off",
};

const ARIA = {
  [TORCH_MODES.AUTO]: "Flash: automatic",
  [TORCH_MODES.ON]: "Flash: manual, on",
  [TORCH_MODES.OFF]: "Flash: off",
};

// Lifted well clear of the bottom controls, which is where the record button and
// the rest of the capture UI live in every one of these modules.
const BOTTOM = "calc(20vh + env(safe-area-inset-bottom, 0px))";

/*
 * Hard against the left edge, with the safe-area inset added so a notch or a
 * rounded corner cannot clip it.
 *
 * The badge is derived from the same numbers rather than given its own literal.
 * They were 12px and 62px, an implicit "button width plus a gap" that nothing
 * enforced -- move one and the badge silently detaches from the button.
 */
const EDGE = 4;
const SIZE = 42;
const GAP = 8;
const LEFT = `calc(${EDGE}px + env(safe-area-inset-left, 0px))`;
const BADGE_LEFT = `calc(${EDGE + SIZE + GAP}px + env(safe-area-inset-left, 0px))`;

const button = {
  position: "absolute",
  left: LEFT,
  bottom: BOTTOM,
  zIndex: 20,
  width: `${SIZE}px`,
  height: `${SIZE}px`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: "none",
  borderRadius: "50%",
  background: "rgba(0, 0, 0, 0.42)",
  WebkitBackdropFilter: "blur(10px)",
  backdropFilter: "blur(10px)",
  cursor: "pointer",
  pointerEvents: "auto",
  touchAction: "manipulation",
  WebkitTapHighlightColor: "transparent",
};

const badgeStyle = {
  position: "absolute",
  left: BADGE_LEFT,
  bottom: BOTTOM,
  height: `${SIZE}px`,
  zIndex: 20,
  display: "flex",
  alignItems: "center",
  padding: "0 12px",
  borderRadius: "999px",
  background: "rgba(0, 0, 0, 0.42)",
  WebkitBackdropFilter: "blur(10px)",
  backdropFilter: "blur(10px)",
  color: "#fff",
  font: "600 12px/1 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  letterSpacing: "0.3px",
  whiteSpace: "nowrap",
  pointerEvents: "none",
  animation: `torch-badge ${BADGE_MS}ms ease both`,
};

// Lightning bolt, narrow enough to leave room for the mode letter beside it.
const BOLT = "M13.6 2.2 L5.6 13.6 h4.5 l-1.3 8.2 7.9-11.4 h-4.5 Z";

/**
 * Flash glyph.
 *
 * The letter sits beside the bolt rather than inside it -- a bolt is far too
 * narrow to hold one legibly at 22px, which is also how phone cameras place it.
 * Lit states fill the bolt; off leaves it outlined, which reads lighter and suits
 * an unlit lamp.
 *
 * The off slash deliberately runs the opposite diagonal to the bolt. Drawn the
 * same way, the two lie almost parallel and merge into one unreadable smear.
 */
const FlashIcon = ({ mode }) => {
  if (mode === TORCH_MODES.OFF) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d={BOLT}
          fill="none"
          stroke={IDLE}
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <g strokeLinecap="round">
          {/* Backing stroke in the button's own dark, so the slash cuts through
              the glyph rather than lying on top of it. */}
          <line x1="4.2" y1="3.8" x2="19.8" y2="20.2" stroke={DARK} strokeWidth="3.6" />
          <line x1="4.2" y1="3.8" x2="19.8" y2="20.2" stroke={IDLE} strokeWidth="1.7" />
        </g>
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path d={BOLT} fill={ACTIVE} />
      <text
        x="19.4"
        y="15.4"
        fill={ACTIVE}
        textAnchor="middle"
        style={{
          font: "700 9px/1 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        {mode === TORCH_MODES.AUTO ? "A" : "M"}
      </text>
    </svg>
  );
};

const TorchModeControl = () => {
  const [state, setState] = useState(() => getTorchState());
  // { text, seq }. seq changes on every tap so React remounts the node and the
  // animation replays from the top instead of leaving a half-faded badge.
  const [badge, setBadge] = useState(null);
  const badgeTimer = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      // Unknown means the track had published no capabilities when it was set;
      // keep asking, since the answer decides whether this button exists at all.
      // With the feature off there is nothing to settle, so the poll stays idle
      // rather than asking a question that has no answer.
      const live = getTorchState();
      if (live.enabled && live.supported === null) refreshTorchSupport();
      const next = getTorchState();
      setState((prev) =>
        prev.mode === next.mode &&
        prev.on === next.on &&
        prev.supported === next.supported &&
        prev.enabled === next.enabled
          ? prev
          : next,
      );
    }, POLL_MS);
    return () => clearInterval(id);
  }, []);

  // The camera can be left while a badge is still up; without this the timer
  // would fire into an unmounted component.
  useEffect(() => () => clearTimeout(badgeTimer.current), []);

  // Off for this client: no button, and nothing behind it either -- see
  // torchController, which does not even probe the camera in that case.
  if (!state.enabled) return null;
  // Compared against false explicitly: null means "not answered yet", and hiding
  // on that would make the button flicker away during camera startup.
  if (state.supported === false) return null;

  const { mode } = state;

  const cycle = async () => {
    const next = NEXT[mode];
    setState(await setTorchMode(next));
    setBadge((prev) => ({
      text: BADGE[next],
      seq: (prev ? prev.seq : 0) + 1,
    }));
    clearTimeout(badgeTimer.current);
    badgeTimer.current = setTimeout(() => setBadge(null), BADGE_MS);
  };

  return (
    <>
      <button
        style={button}
        aria-label={ARIA[mode] || ARIA[TORCH_MODES.AUTO]}
        onClick={cycle}
      >
        <FlashIcon mode={mode} />
      </button>
      {badge ? (
        <div key={badge.seq} style={badgeStyle}>
          {badge.text}
        </div>
      ) : null}
    </>
  );
};

export default TorchModeControl;
